import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ChevronDown, ChevronRight, Network, RefreshCw, X } from "lucide-react";
import { type HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CompanyMember } from "../../api/companiesApi";
import type { Department } from "../../api/departmentsApi";
import { useAuthedApi } from "../../api/useAuthedApi";
import { type AuthUser, hasPermission } from "../../auth/useAuth";
import { EmptyState } from "../../components/Common/EmptyState";
import { PageHeader } from "../../components/Common/PageHeader";
import { WorkspaceCard } from "../../components/Common/WorkspaceCard";

function message(e: unknown): string {
  return e instanceof Error && e.message ? e.message : "Something went wrong";
}

interface DeptRoster {
  members: string[];
  editors: string[];
}

function uniq(ids: string[]): string[] {
  return [...new Set(ids)];
}

function patchDeptRoster(
  prev: Record<number, DeptRoster>,
  deptId: number,
  patch: (roster: DeptRoster) => DeptRoster,
): Record<number, DeptRoster> {
  const current = prev[deptId] ?? { members: [], editors: [] };
  return { ...prev, [deptId]: patch(current) };
}

/** Resolve a display label from the company member list, falling back to id. */
function memberLabel(members: CompanyMember[], userId: string): string {
  return members.find((m) => m.user_id === userId)?.email ?? userId;
}

/** Removable user chips for a department roster row. */
function UserChipList({
  userIds,
  members,
  busy,
  removeLabel,
  onRemove,
}: {
  userIds: string[];
  members: CompanyMember[];
  busy: boolean;
  removeLabel: string;
  onRemove: (userId: string) => void;
}) {
  if (userIds.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
      {userIds.map((uid) => {
        const label = memberLabel(members, uid);
        return (
          <Chip
            key={uid}
            size="small"
            label={label}
            onDelete={busy ? undefined : () => onRemove(uid)}
            deleteIcon={<X data-ph="delete" size={14} aria-label={`${removeLabel} ${label}`} />}
          />
        );
      })}
    </Box>
  );
}

/** Add-users row: searchable multi-select from the company member list; falls
 *  back to a free-text user id when the member list isn't available. */
function UserPicker({
  members,
  label,
  excludeUserIds = [],
  onSubmit,
  busy,
  popperScope,
}: {
  members: CompanyMember[];
  label: string;
  excludeUserIds?: string[];
  onSubmit: (userIds: string[]) => void;
  busy: boolean;
  /** Full path of the section the picker sits in: its options render in a
   *  portaled Popper outside the page's scopes (D7). A string literal. */
  popperScope: string;
}) {
  const [selected, setSelected] = useState<CompanyMember[]>([]);
  const [manualUserId, setManualUserId] = useState("");
  const excluded = new Set(excludeUserIds);
  const available = members.filter((m) => !excluded.has(m.user_id));

  const submit = (userIds: string[]) => {
    if (userIds.length === 0) return;
    onSubmit(userIds);
    setSelected([]);
    setManualUserId("");
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      {members.length > 0 ? (
        <Autocomplete
          data-ph="open"
          multiple
          size="small"
          options={available}
          value={selected}
          onChange={(_e, value) => setSelected(value)}
          getOptionLabel={(m) => m.email ?? m.user_id}
          isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
          filterSelectedOptions
          disabled={busy}
          sx={{ minWidth: 280, flex: 1, maxWidth: 480 }}
          // Options are tagged by kind on the listbox (never the user's email).
          // Chips and the clear button sit inside the root, so each carries its
          // own tag rather than reading as the root's `open`.
          slotProps={{
            popper: { "data-ph-scope": popperScope } as object,
            listbox: { "data-ph": "select" } as HTMLAttributes<HTMLUListElement>,
            chip: { "data-ph": "delete" } as Record<string, string>,
            clearIndicator: { "data-ph": "delete_all" } as Record<string, string>,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder="Search by email…"
              inputProps={{
                ...params.inputProps,
                "aria-label": label,
              }}
            />
          )}
        />
      ) : (
        <TextField
          size="small"
          label={label}
          placeholder="user id"
          value={manualUserId}
          disabled={busy}
          onChange={(e) => setManualUserId(e.target.value)}
          sx={{ minWidth: 220 }}
        />
      )}
      <Button
        data-ph="assign"
        size="small"
        variant="outlined"
        disabled={
          busy || (members.length > 0 ? selected.length === 0 : !manualUserId.trim())
        }
        onClick={() =>
          submit(
            members.length > 0
              ? selected.map((m) => m.user_id)
              : [manualUserId.trim()],
          )
        }
      >
        Add
      </Button>
    </Box>
  );
}

/** Departments admin content. Exported bare for unit tests; the route/section
 *  renders it inside the shared layout like CompanyMembersPage. */
export function DepartmentsPage({ embedded = false }: { embedded?: boolean }) {
  const { departments, companies, auth, orgSync, connectors, customMcp } = useAuthedApi();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [depts, setDepts] = useState<Department[] | null>(null);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [rosters, setRosters] = useState<Record<number, DeptRoster>>({});
  const [allDeptsEditors, setAllDeptsEditors] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [hasConnectedMcp, setHasConnectedMcp] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const rosterFetchSeq = useRef<Record<number, number>>({});

  // Create form
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState<number | "">("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await auth.me();
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setUserLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const isAdmin = hasPermission(user, "company:manage");

  const load = useCallback(async () => {
    try {
      setDepts(await departments.list());
    } catch (e) {
      setError(message(e));
      setDepts([]);
    }
    try {
      const m = await companies.membersAll();
      setMembers(m);
    } catch {
      // No member list — the pickers fall back to free-text user ids.
      setMembers([]);
    }
    try {
      const editors = await departments.listAllDeptsEditors();
      setAllDeptsEditors(editors.map((e) => e.user_id));
    } catch {
      // Keep session roster when the list endpoint is unavailable.
    }
    try {
      // Marketplace OAuth connectors and Plugin MCP (company + personal)
      // are different APIs. Org-sync can use either.
      const [market, companyMcp, userMcp] = await Promise.allSettled([
        connectors.list(),
        customMcp.list("company"),
        customMcp.list("user"),
      ]);
      const marketHit =
        market.status === "fulfilled" &&
        market.value.some((c) => c.state === "connected");
      const pluginHit = [companyMcp, userMcp].some(
        (r) =>
          r.status === "fulfilled" &&
          r.value.some((c) => c.connected && c.enabled !== false),
      );
      const probed =
        market.status === "fulfilled" ||
        companyMcp.status === "fulfilled" ||
        userMcp.status === "fulfilled";
      setHasConnectedMcp(marketHit || pluginHit || !probed);
    } catch {
      setHasConnectedMcp(true);
    }
  }, [departments, companies, connectors, customMcp]);

  const loadDeptRoster = useCallback(
    async (deptId: number) => {
      const seq = (rosterFetchSeq.current[deptId] ?? 0) + 1;
      rosterFetchSeq.current[deptId] = seq;
      try {
        const [memberRows, editorRows] = await Promise.all([
          departments.listMembers(deptId),
          departments.listEditors(deptId),
        ]);
        if (rosterFetchSeq.current[deptId] !== seq) return;
        const fromServer: DeptRoster = {
          members: memberRows.map((m) => m.user_id),
          editors: editorRows.map((e) => e.user_id),
        };
        setRosters((prev) => {
          const current = prev[deptId] ?? { members: [], editors: [] };
          return {
            ...prev,
            [deptId]: {
              // FGA reads can degrade to empty while mutations succeed; keep the
              // in-session roster rather than wiping chips the user just added.
              members:
                fromServer.members.length > 0 || current.members.length === 0
                  ? fromServer.members
                  : current.members,
              editors:
                fromServer.editors.length > 0 || current.editors.length === 0
                  ? fromServer.editors
                  : current.editors,
            },
          };
        });
      } catch {
        // Keep existing roster — the GET endpoints may be missing until the
        // agent service is restarted after deploy.
      }
    },
    [departments],
  );

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  useEffect(() => {
    if (expanded != null) void loadDeptRoster(expanded);
  }, [expanded, loadDeptRoster]);

  /** Run an admin mutation, surfacing errors and refreshing the list. */
  async function run(
    action: () => Promise<void>,
    done: string,
    opts?: {
      deptId?: number;
      refreshAllDeptsEditors?: boolean;
      rosterPatch?: (roster: DeptRoster) => DeptRoster;
      allDeptsEditorsPatch?: (ids: string[]) => string[];
    },
  ) {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await action();
      if (opts?.rosterPatch && opts.deptId != null) {
        setRosters((prev) => patchDeptRoster(prev, opts.deptId!, opts.rosterPatch!));
      }
      if (opts?.allDeptsEditorsPatch) {
        setAllDeptsEditors((prev) => opts.allDeptsEditorsPatch!(prev));
      }
      setNotice(done);
      await load();
      if (opts?.deptId != null) await loadDeptRoster(opts.deptId);
      if (opts?.refreshAllDeptsEditors) {
        try {
          const editors = await departments.listAllDeptsEditors();
          setAllDeptsEditors(editors.map((e) => e.user_id));
        } catch {
          // Keep optimistic all-depts editor chips.
        }
      }
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  async function onCreate() {
    await run(async () => {
      await departments.create(name.trim(), parentId === "" ? undefined : parentId);
      setName("");
      setParentId("");
    }, "Department created");
  }

  const byId = useMemo(
    () => new Map((depts ?? []).map((d) => [d.id, d.name])),
    [depts],
  );

  if (userLoaded && !isAdmin) {
    return (
      <>
        {!embedded && <PageHeader title="Departments" />}
        <Alert severity="warning">
          Only a company admin can manage departments.
        </Alert>
      </>
    );
  }

  return (
    // The Departments tab's section scope; display: contents keeps it out of layout.
    <div data-ph-scope="departments" style={{ display: "contents" }}>
      {!embedded && (
        <PageHeader
          title="Departments"
          subtitle="Structure your organization and delegate skill editing."
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {/* ── Create ── */}
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          void onCreate();
        }}
        sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 3, flexWrap: "wrap" }}
      >
        <TextField
          size="small"
          label="Department name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <FormControl data-ph-scope="parent" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Parent (optional)</InputLabel>
          <Select
            data-ph="open"
            label="Parent (optional)"
            value={parentId}
            onChange={(e) => setParentId(e.target.value as number | "")}
            // The menu is portaled out of the form, so it names its whole path (D7).
            MenuProps={{ slotProps: { root: { "data-ph-scope": "organization-departments-parent" } as object } }}
          >
            <MenuItem data-ph="select" value="">
              <em>None</em>
            </MenuItem>
            {(depts ?? []).map((d) => (
              <MenuItem data-ph="select" key={d.id} value={d.id}>
                {d.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button data-ph="create" type="submit" variant="contained" disabled={busy || fetching || !name.trim()}>
          Create department
        </Button>
        <Button
          data-ph="import"
          variant="outlined"
          disabled={busy || fetching || !hasConnectedMcp}
          title={
            !hasConnectedMcp
              ? "Connect a plugin MCP or marketplace tool first"
              : undefined
          }
          startIcon={
            fetching ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <RefreshCw size={16} />
            )
          }
          onClick={() => {
            void (async () => {
              setError(null);
              setNotice(null);
              setFetching(true);
              try {
                await orgSync.run();
                setNotice("Fetch complete");
                await load();
                if (expanded != null) await loadDeptRoster(expanded);
              } catch (e) {
                setError(message(e));
              } finally {
                setFetching(false);
              }
            })();
          }}
        >
          {fetching ? "Loading" : "Fetch Org details"}
        </Button>
      </Box>

      {/* ── List ── */}
      {depts === null ? null : depts.length === 0 ? (
        <EmptyState
          icon={Network}
          title="No departments yet"
          description="Create your first department to scope skills and membership."
        />
      ) : (
        <Stack spacing={1.5} sx={{ mb: 4 }}>
          {depts.map((d) => {
            const open = expanded === d.id;
            const roster = rosters[d.id] ?? { members: [], editors: [] };
            return (
              <WorkspaceCard key={d.id} interactive={false}>
                <Box data-ph-scope="department">
                <Box
                  data-ph={open ? "collapse" : "expand"}
                  sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
                  onClick={() => setExpanded(open ? null : d.id)}
                  role="button"
                  aria-expanded={open}
                  aria-label={`toggle ${d.name}`}
                >
                  {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <Typography sx={{ fontWeight: 600 }}>{d.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    #{d.id}
                    {` · ${d.member_count ?? 0} ${
                      (d.member_count ?? 0) === 1 ? "member" : "members"
                    }`}
                    {d.parent_id != null &&
                      ` · parent: ${byId.get(d.parent_id) ?? d.parent_id}`}
                  </Typography>
                </Box>
                <Collapse in={open} unmountOnExit>
                  <Stack spacing={2} sx={{ mt: 2, pl: 3 }}>
                    <Box data-ph-scope="members">
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Members
                      </Typography>
                      <UserChipList
                        userIds={roster.members}
                        members={members}
                        busy={busy}
                        removeLabel="Remove member"
                        onRemove={(uid) =>
                          void run(
                            () => departments.removeMember(d.id, uid),
                            "Member removed",
                            {
                              deptId: d.id,
                              rosterPatch: (r) => ({
                                ...r,
                                members: r.members.filter((m) => m !== uid),
                              }),
                            },
                          )
                        }
                      />
                      <UserPicker
                        members={members}
                        label="Add member"
                        popperScope="organization-departments-department-members"
                        excludeUserIds={roster.members}
                        busy={busy}
                        onSubmit={(userIds) =>
                          void run(
                            async () => {
                              for (const uid of userIds) {
                                await departments.addMember(d.id, uid);
                              }
                            },
                            userIds.length === 1
                              ? "Member added"
                              : `${userIds.length} members added`,
                            {
                              deptId: d.id,
                              rosterPatch: (r) => ({
                                ...r,
                                members: uniq([...r.members, ...userIds]),
                              }),
                            },
                          )
                        }
                      />
                    </Box>
                    <Box data-ph-scope="editors">
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Skill editors
                      </Typography>
                      <UserChipList
                        userIds={roster.editors}
                        members={members}
                        busy={busy}
                        removeLabel="Remove skill editor"
                        onRemove={(uid) =>
                          void run(
                            () => departments.removeEditor(d.id, uid),
                            "Editor removed",
                            {
                              deptId: d.id,
                              rosterPatch: (r) => ({
                                ...r,
                                editors: r.editors.filter((m) => m !== uid),
                              }),
                            },
                          )
                        }
                      />
                      <UserPicker
                        members={members}
                        label="Add skill editor"
                        popperScope="organization-departments-department-editors"
                        excludeUserIds={roster.editors}
                        busy={busy}
                        onSubmit={(userIds) =>
                          void run(
                            async () => {
                              for (const uid of userIds) {
                                await departments.addEditor(d.id, uid);
                              }
                            },
                            userIds.length === 1
                              ? "Editor added"
                              : `${userIds.length} editors added`,
                            {
                              deptId: d.id,
                              rosterPatch: (r) => ({
                                ...r,
                                editors: uniq([...r.editors, ...userIds]),
                              }),
                            },
                          )
                        }
                      />
                    </Box>
                  </Stack>
                </Collapse>
                </Box>
              </WorkspaceCard>
            );
          })}
        </Stack>
      )}

      {/* ── All-departments skills editors ── */}
      <Box data-ph-scope="all_editors">
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          All-departments skills editors
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          These users can edit skills in every department (current and future).
        </Typography>
        <Stack spacing={1}>
          <UserChipList
            userIds={allDeptsEditors}
            members={members}
            busy={busy}
            removeLabel="Remove all-departments editor"
            onRemove={(uid) =>
              void run(
                () => departments.removeAllDeptsEditor(uid),
                "All-departments editor removed",
                {
                  refreshAllDeptsEditors: true,
                  allDeptsEditorsPatch: (ids) => ids.filter((id) => id !== uid),
                },
              )
            }
          />
          <UserPicker
            members={members}
            label="Add all-departments editor"
            popperScope="organization-departments-all_editors"
            excludeUserIds={allDeptsEditors}
            busy={busy}
            onSubmit={(userIds) =>
              void run(
                async () => {
                  for (const uid of userIds) {
                    await departments.addAllDeptsEditor(uid);
                  }
                },
                userIds.length === 1
                  ? "All-departments editor added"
                  : `${userIds.length} all-departments editors added`,
                {
                  refreshAllDeptsEditors: true,
                  allDeptsEditorsPatch: (ids) => uniq([...ids, ...userIds]),
                },
              )
            }
          />
        </Stack>
      </Box>
    </div>
  );
}
